// OpenRouter API service for generating detailed reports
const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';

export const generateDetailedReport = async (userData, submissionData, incentivesData, openRouterApiKey) => {
  try {
    const prompt = `You are a professional producer advisor with 40 years of experience in the film and television production industry. You have extensive knowledge of navigating production finance areas and are an expert in tax incentives, rebates, and credits globally.

CRITICAL MISSION: Analyze the user's script and project details to recommend OPTIMAL FILMING LOCATIONS WORLDWIDE that perfectly match the creative and logistical requirements while offering the BEST available incentives. Your recommendations should be INCENTIVE-FOCUSED, not geographically limited.

USER PROJECT ANALYSIS:
- **Project Title**: ${submissionData?.parsed_metadata?.working_title || 'Untitled Project'}
- **Production Type**: ${submissionData?.parsed_metadata?.production_type || 'Feature Film'}
- **Genre**: ${submissionData?.parsed_metadata?.genre?.join(', ') || 'N/A'}
- **Original Setting Countries**: ${submissionData?.parsed_metadata?.setting_countries?.join(', ') || 'N/A'}
- **Setting Cities**: ${submissionData?.parsed_metadata?.setting_cities?.join(', ') || 'N/A'}
- **Historical Period**: ${submissionData?.parsed_metadata?.period_years ? `${submissionData.parsed_metadata.period_years.start} - ${submissionData.parsed_metadata.period_years.end}` : 'Contemporary'}
- **Primary Language**: ${submissionData?.parsed_metadata?.primary_language || 'English'}
- **Expected Runtime**: ${submissionData?.parsed_metadata?.expected_runtime_minutes || '120'} minutes
- **Child Actors Required**: ${submissionData?.parsed_metadata?.presence_of_minors ? 'Yes' : 'No'}
- **VFX/Stunts Intensity**: ${submissionData?.parsed_metadata?.stunts_or_vfx_intensity || 'Medium'}
- **Local Spend Commitment**: ${submissionData?.parsed_metadata?.likely_spend_split_local_percent || 60}%
- **Total Production Budget**: $${submissionData?.budget_amount?.toLocaleString() || '10,000,000'} USD

GLOBAL LOCATION ANALYSIS REQUIREMENT:
Based on the script's creative and logistical needs, you MUST:
1. **ANALYZE the script's requirements** for locations, climate, infrastructure, cultural authenticity, and production feasibility
2. **IDENTIFY optimal filming locations worldwide** that can replicate or improve upon the original setting while offering superior incentives
3. **PRIORITIZE locations with the BEST incentive programs** (highest rated, most generous terms, easiest to qualify for)
4. **CONSIDER practical production factors** like crew availability, equipment access, post-production facilities, and transportation logistics
5. **EVALUATE incentive program quality** based on ratings, ease of use, payment reliability, and production support services

AI-GENERATED INCENTIVE MATCHES (Use ALL of these in your analysis):
${incentivesData?.slice(0, 10).map((incentive, index) => `
${index + 1}. ${incentive.jurisdiction_name} - ${incentive.incentive_name}
   - Type: ${incentive.incentive_type}
   - Rate: ${incentive.base_rate_percent}%
   - Estimated Savings: $${incentive.estimated_savings?.toLocaleString() || '0'} USD
   - Rating: ${incentive.mark_stars}/5 stars
   - Summary: ${incentive.summary_one_liner}
   - Official URL: ${incentive.official_url}
   - Additional Details: ${JSON.stringify(incentive.tooltip)}
`).join('\n')}

INSTRUCTIONS FOR REPORT GENERATION:
1. Act as a seasoned film producer advisor with deep industry knowledge
2. Provide realistic, practical advice based on the specific project details
3. Focus on the top 5-10 most viable incentives, explaining why they are good matches - DO NOT SKIP ANY OF THE TOP INCENTIVES PROVIDED
4. Include specific financial calculations showing potential savings
5. Address any potential challenges or requirements for each incentive
6. Suggest a strategic approach to maximize benefits
7. Include next steps and action items for the producer
8. Keep all currency references in USD
9. Make the advice professional, detailed, and actionable
10. Consider the production's specific characteristics (genre, location, budget, etc.)

CRITICAL USER-SPECIFIC ANALYSIS REQUIREMENTS:
- For "${submissionData?.parsed_metadata?.working_title || 'this project'}" specifically, analyze how each recommended incentive aligns with the project's genre (${submissionData?.parsed_metadata?.genre?.join(', ') || 'drama'})
- Consider the ${submissionData?.budget_amount ? `$${submissionData.budget_amount.toLocaleString()}` : '$1M'} budget size and explain which incentives work best for this budget range
- Address the original setting of ${submissionData?.parsed_metadata?.setting_countries?.join(', ') || 'United States'} and suggest how filming elsewhere might enhance or challenge the production
- Consider the ${submissionData?.parsed_metadata?.expected_runtime_minutes || 120} minute runtime and whether this affects incentive eligibility
- Factor in VFX requirements (${submissionData?.parsed_metadata?.stunts_or_vfx_intensity || 'medium'} intensity) and which locations offer best post-production facilities
- Discuss language considerations for ${submissionData?.parsed_metadata?.primary_language || 'English'} dialogue and potential dubbing/subtitle requirements
- Address the ${submissionData?.parsed_metadata?.likely_spend_split_local_percent || 60}% local spend commitment and how to optimize this for each location

STRUCTURE THE REPORT AS FOLLOWS:

1. EXECUTIVE SUMMARY
   - Brief overview of the best opportunities for "${submissionData?.parsed_metadata?.working_title || 'this project'}"
   - **Why it's a Top Choice:** Explain why the primary recommended location is ideal for this specific project
   - **Project Fit:** How this project's genre (${submissionData?.parsed_metadata?.genre?.join(', ') || 'drama'}), budget, and requirements align with the recommended incentives
   - **Challenge:** One key challenge this project might face and how the recommended incentives help overcome it

2. PROJECT ANALYSIS
   - Assessment of this specific project's incentive eligibility and unique characteristics
   - Analysis of how the project's ${submissionData?.parsed_metadata?.production_type || 'feature'} format, ${submissionData?.parsed_metadata?.runtime || 120} minute runtime, and ${submissionData?.parsed_metadata?.genre?.join('/') || 'drama'} genre affect incentive eligibility
   - Evaluation of local spend requirements (${submissionData?.parsed_metadata?.likely_spend_split_local_percent || 60}%) and how they impact the recommendations

3. TOP INCENTIVE RECOMMENDATIONS
   - DETAILED breakdown of ALL top matches with project-specific reasoning (include AT LEAST the top 5, preferably top 8-10)
   - For each recommendation, explain WHY it's suitable for this specific project based on genre, budget, location, and technical requirements

4. FINANCIAL IMPACT ANALYSIS
   - Specific savings calculations based on the actual ${submissionData?.budget_amount ? `$${submissionData.budget_amount.toLocaleString()}` : '$1M'} budget
   - Detailed breakdown showing local spend amounts, incentive rates, and total potential savings for each recommended location

5. STRATEGIC RECOMMENDATIONS
   - How to maximize benefits for this ${submissionData?.parsed_metadata?.genre?.[0] || 'drama'} genre and ${submissionData?.budget_amount ? `$${submissionData.budget_amount.toLocaleString()}` : '$1M'} budget level
   - Specific strategies for optimizing local spend and maximizing incentive benefits

6. NEXT STEPS - VERY DETAILED TIMELINE
   - **Week 1-2: Foundation Building**
     - Budget breakdown for your top 3 locations with specific percentages and dollar amounts based on the ${submissionData?.budget_amount ? `$${submissionData.budget_amount.toLocaleString()}` : '$1M'} budget
     - Include local spend allocation for each recommended location
     - Contact information and initial consultation steps for film commissions

   - **Week 3: Detailed Research**
     - Research each recommended location's specific requirements and application processes
     - Include direct links to official film commission websites for each location
     - Compare infrastructure, crew availability, and post-production facilities

   - **Week 4: Planning & Preparation**
     - Location scouting coordination and site visits
     - Local crew and vendor identification and contracts
     - Legal and financial planning with incentive specialists

   - **Month 2: Application & Pre-Production**
     - Complete formal applications for selected locations
     - Note: For some locations, applications must be submitted before shooting begins
     - Secure budget commitments and finalize production planning

7. POTENTIAL CHALLENGES
   - **Cultural Tests:** Specific content requirements for each recommended location and how to meet them for this ${submissionData?.parsed_metadata?.genre?.join('/') || 'drama'} project
   - **Tax Credit Monetization:** How to effectively use and monetize tax credits, timing considerations, and cash flow management
   - **Currency Fluctuation:** Impact on ${submissionData?.budget_amount ? `$${submissionData.budget_amount.toLocaleString()}` : '$1M'} budget and risk mitigation strategies
   - **Distance from ${submissionData?.parsed_metadata?.setting_countries?.[0] || 'US'}:** Logistics, travel, communication, and timezone challenges for international production

8. CONCLUSION
   - Final thoughts tailored to "${submissionData?.parsed_metadata?.working_title || 'this project'}" and encouragement
   - Summary of the best path forward and expected ROI

CRITICAL REQUIREMENTS:
- Fill in EVERY section with specific, relevant content - DO NOT leave sections blank or incomplete
- Use actual location names and URLs from the AI-GENERATED INCENTIVE MATCHES data above
- Reference the project's specific details (${submissionData?.parsed_metadata?.working_title || 'project title'}, ${submissionData?.parsed_metadata?.genre?.join('/') || 'genre'}, ${submissionData?.budget_amount ? `$${submissionData.budget_amount.toLocaleString()}` : '$1M'} budget) throughout the entire report
- Include realistic timelines and specific dollar amounts based on the actual budget
- Make all recommendations project-specific, not generic - tailor every suggestion to this specific production
- Ensure all links and contact information are accurate and taken from the provided incentive data
- For the NEXT STEPS section, include actual URLs from the incentive data for each recommended location
- Calculate and include specific budget breakdowns for the top 3 locations in Week 1-2

IMPORTANT: The AI-GENERATED INCENTIVE MATCHES section above contains the exact URLs and location details you must use. Reference these URLs in the Week 3 research section and throughout the report.

Make this report comprehensive yet readable, professional in tone, and extremely valuable to a film producer. Include specific numbers, realistic timelines, and practical advice throughout. EVERY recommendation should reference the specific project details provided.`;

    const response = await fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${openRouterApiKey}`,
        'HTTP-Referer': window.location.origin,
        'X-Title': 'NuPitch Incentive Match Maker'
      },
      body: JSON.stringify({
        model: 'openai/gpt-5-mini',
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 6000,
        top_p: 0.9
      })
    });

    if (!response.ok) {
      throw new Error(`OpenRouter API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.choices || !data.choices[0] || !data.choices[0].message) {
      throw new Error('Invalid response from OpenRouter API');
    }

    return data.choices[0].message.content;
  } catch (error) {
    console.error('OpenRouter API error:', error);
    throw new Error(`Failed to generate report: ${error.message}`);
  }
};

export const generateReportWithProgress = async (userData, submissionData, incentivesData, openRouterApiKey, onProgress) => {
  // Simulate progress updates
  onProgress?.(10, 'Analyzing project details...');
  await new Promise(resolve => setTimeout(resolve, 500));

  onProgress?.(25, 'Evaluating incentive matches...');
  await new Promise(resolve => setTimeout(resolve, 500));

  onProgress?.(40, 'Generating detailed analysis...');
  await new Promise(resolve => setTimeout(resolve, 500));

  onProgress?.(60, 'Creating financial projections...');
  await new Promise(resolve => setTimeout(resolve, 500));

  onProgress?.(80, 'Finalizing recommendations...');

  try {
    const reportContent = await generateDetailedReport(userData, submissionData, incentivesData, openRouterApiKey);
    onProgress?.(100, 'Report generated successfully!');
    return reportContent;
  } catch (error) {
    onProgress?.(0, 'Error generating report');
    throw error;
  }
};
